/**
 * 폰 Room 브릿지 → 기존 컴포넌트 타입(CallListResponse/CallDetail) 어댑터.
 *
 * privacy-first 재설계: 통화 데이터는 서버가 아니라 폰 Room에서 온다(D5). 브릿지 JSON은
 * camelCase + epoch millis라, 컴포넌트가 이미 쓰는 snake_case + ISO 문자열 형태로 변환해
 * Calls/CallDetail/Home의 데이터 출처만 바꾼다(컴포넌트 본문은 미변경).
 *
 * NOTE: 지금은 일회성 request(). Room 변경 실시간 push(observe)는 Hero Loop 카드(P6)에서 연동.
 */
import type { CallDetail, CallListResponse, SmsListResponse, SpeakerSegment } from './api';
import { room } from './roomBridge';

interface RawSms {
  id: string;
  receivedAt: number;
  fromPhone: string | null;
  body: string;
  direction: 'in' | 'out';
}

interface RawCall {
  id: string;
  startedAt: number;
  durationSec: number | null;
  contactPhone: string | null;
  contactName: string | null;
  direction: 'in' | 'out' | 'missed' | null;
  category: 'work' | 'service' | 'personal' | 'etc' | null;
  status: CallDetail['call']['status'];
  error?: string | null;
}
interface RawDetail {
  call: RawCall | null;
  transcript: {
    id: string;
    fullText: string;
    speakerSegments: SpeakerSegment[] | null;
    provider: string;
  } | null;
  summary: {
    id: string;
    topic: string | null;
    bullets: string[] | null;
    followups: string[] | null;
    model: string;
  } | null;
}

/** epoch-ms → ISO. 손상된 payload(undefined/NaN)에도 throw 안 하도록 가드. */
function isoFrom(ms: number): string {
  const n = typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
  return new Date(n).toISOString();
}

function toListItem(c: RawCall) {
  return {
    id: c.id,
    started_at: isoFrom(c.startedAt),
    duration_seconds: c.durationSec ?? null,
    contact_phone: c.contactPhone ?? null,
    contact_name: c.contactName ?? null,
    direction: c.direction ?? null,
    category: c.category ?? null,
    status: c.status,
  };
}

export async function listCallsFromRoom(limit = 50): Promise<CallListResponse> {
  const rows = (await room.calls.list(limit)) as RawCall[];
  return { items: rows.map(toListItem), next_cursor: null };
}

export async function listSmsFromRoom(limit = 100): Promise<SmsListResponse> {
  const rows = (await room.sms.list(limit)) as RawSms[];
  return {
    items: rows.map((s) => ({
      id: s.id,
      received_at: isoFrom(s.receivedAt),
      from_phone: s.fromPhone ?? null,
      body: s.body,
      direction: s.direction,
    })),
    next_cursor: null,
  };
}

export async function getCallFromRoom(id: string): Promise<CallDetail | null> {
  const d = (await room.calls.get(id)) as RawDetail | null;
  if (!d || !d.call) return null;
  const c = d.call;
  return {
    call: {
      id: c.id,
      started_at: isoFrom(c.startedAt),
      duration_seconds: c.durationSec ?? null,
      contact_phone: c.contactPhone ?? null,
      contact_name: c.contactName ?? null,
      direction: c.direction ?? null,
      category: c.category ?? null,
      status: c.status,
      error: c.error ?? null,
    },
    transcript: d.transcript
      ? {
          id: d.transcript.id,
          fullText: d.transcript.fullText,
          speakerSegments: d.transcript.speakerSegments ?? [],
          provider: d.transcript.provider,
        }
      : null,
    summary: d.summary
      ? {
          id: d.summary.id,
          topic: d.summary.topic ?? null,
          bullets: d.summary.bullets ?? null,
          followups: d.summary.followups ?? null,
          model: d.summary.model,
        }
      : null,
  };
}
